import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { employeeColor, secondsToHours } from './chartUtils';
import type { GlobalSummaryProject } from '~/common/api/zeiterfassungServerApi/ProjectRecordApi';
import useTranslate from '~/common/utils/translate';

interface Props {
  data: GlobalSummaryProject[];
}

const MIN_BAR_WIDTH = 60;

export const HoursByEmployeeChart: React.FC<Props> = ({ data }) => {
  const t = useTranslate();
  const { chartData, employeeNames } = useMemo(() => {
    const nameSet = new Set<string>();
    const rows = data.map((p) => {
      const row: Record<string, string | number> = { project: p.project_name };
      p.employees.forEach((e) => {
        const name = e.employee_name || `#${e.employee_id}`;
        nameSet.add(name);
        row[name] = secondsToHours(e.total_duration_seconds);
      });
      return row;
    });
    return { chartData: rows, employeeNames: Array.from(nameSet) };
  }, [data]);

  const chartWidth = Math.max(chartData.length * MIN_BAR_WIDTH, 400);

  return (
    <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={3}>
      <Text fontSize="sm" fontWeight="600" mb={2}>
        {t('projectRecords.chart.hoursByEmployee')}
      </Text>
      <Box overflowX="auto">
        <Box minW={`${chartWidth}px`} h="250px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="project" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="h" />
              <Tooltip />
              <Legend />
              {employeeNames.map((name, i) => (
                <Bar key={name} dataKey={name} stackId="hours" fill={employeeColor(i)} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};
